import { useState, useMemo, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Autocomplete, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, LinearProgress, MenuItem, Paper, Stack, Tab, Tabs,
  Tooltip, Typography,
} from '@mui/material'
import { LocationIcon, IndustryIcon } from '../components/shared/CustomIcons'
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Sell as SourceIcon,
  Business as BusinessIcon,
  Language as LanguageIcon,
  OpenInNew as OpenInNewIcon,
  Check as CheckIcon,
  CalendarToday as DateIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
  ChatBubbleOutline as CommentIcon,
  KeyboardArrowDown as ArrowDownIcon,
  CalendarMonth as CalendarIcon,
  LocalOffer as TagIcon,
  Work as WorkIcon,
  Link as LinkIcon,
  InsertDriveFile as FileIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material'
import { Schedule as FollowUpTabIcon } from '@mui/icons-material'
import { toast } from 'react-toastify'
import {
  leadsApi, tagsApi, Tag,
  Lead, LeadStatus, LeadSource,
  UpdateLeadPayload, AddActivityPayload,
  LEAD_SOURCES, SOURCE_LABELS, STATUS_LABELS, MANUAL_ACTIVITY_TYPES, INDUSTRY_TYPES,
} from '../lib/api/leadsApi'
import { getCustomerTypes, CustomerType } from '../lib/api/customerTypesApi'
import { usersAdminApi } from '../lib/api/usersAdminApi'
import { themeColors } from '../app/layout/theme'
import { authStore } from '../lib/stores/authStore'
import { observer } from 'mobx-react-lite'
import { maskPhoneNumber } from '../utils/phoneUtils'

import {
  UniversalActivityTimeline, StatusBadge,
} from '../components/shared/UniversalActivityTimeline'
import { CommentItem, CommentInput, CommentsEmptyState } from '../components/shared/NotesSection'
import { leadNotesApi } from '../lib/api/leadNotesApi'
import LeadEventsSection from '../components/leads/LeadEventsSection'
import LeadTasksSection from '../components/leads/LeadTasksSection'
import { leadTasksApi } from '../lib/api/leadTasksApi'
import { leadFollowUpsApi } from '../lib/api/leadFollowUpsApi'
import AttachmentsSection from '../components/shared/AttachmentsSection'
import { CallHistorySection } from '../components/calls/CallHistorySection'
import { callLogsApi, CallLog, CallStatus, SaveOutcomePayload } from '../lib/api/callLogsApi'
import { callStore } from '../lib/stores/callStore'
import { ConvertLeadFlow } from '../components/leads/ConvertLeadFlow'
import { EditLeadDialog } from '../components/leads/EditLeadDialog'
import { SendEmailDrawer, SendEmailData } from '../components/drawers/SendEmailDrawer'
import { emailApi } from '../lib/api/emailApi'
import { EmailHistorySection } from '../components/shared/EmailHistorySection'
import { customersApi } from '../lib/api/customersApi'
import { contactsApi } from '../lib/api/contactsApi'
import { SideCard } from '../components/shared/SideCard'
import { RequirementsSideCard } from '../components/shared/RequirementsSideCard'
import { AddressAndTaxInfoCard } from '../components/shared/AddressAndTaxInfoCard'
import { DescriptionSection } from '../components/shared/DescriptionSection'
import { InfoRow } from '../components/shared/DetailViewWrappers'
import { SummaryRow } from '../components/shared/DetailViewWrappers'
import { EntityModal } from '../components/shared/EntityModal'
import { EntityDetailTabs } from '../components/shared/EntityDetailTabs'
import { FormInput, FormSelect } from '../components/ui'
import { ConfirmDialog } from '../components/shared/ConfirmDialog'
import { DeleteDialog } from '../components/shared/DeleteDialog'
import { usePermission } from '../lib/hooks/usePermission'
import { HelpAssigneeSelector } from '../components/shared/HelpAssigneeSelector'

// ── Helpers ───────────────────────────────────────────────────────────────────

function getInitials(name: string) {
  return name.split(' ').slice(0, 2).map(n => n[0]?.toUpperCase() ?? '').join('')
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime()) || d.getFullYear() <= 1970) return '—'
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

const AVATAR_COLORS = ['#0070d2', '#2e7d32', '#7b1fa2', '#f57c00', '#c62828', '#00838f']
function avatarColor(name: string) {
  let h = 0; for (const c of name) h = c.charCodeAt(0) + ((h << 5) - h)
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}

// ── Call Flow Hook removed (using global callStore)

// ── Add Tag Modal ─────────────────────────────────────────────────────────────

interface TagMiniItem { id: string; name: string; color: string }

function AddTagModal({ open, onClose, allTags, attachedTagIds, onAdd, isAdding }: {
  open: boolean
  onClose: () => void
  allTags: TagMiniItem[]
  attachedTagIds: Set<string>
  onAdd: (tagId: string) => void
  isAdding: boolean
}) {
  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title="Add Tag"
      icon={<TagIcon sx={{ fontSize: 17 }} />}
    >
      {allTags.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <TagIcon sx={{ fontSize: 36, color: '#e5e7eb', mb: 1 }} />
          <Typography fontSize="13px" color="text.secondary">
            No tags available. Create tags from the Tags page.
          </Typography>
        </Box>
      ) : (
        <Box>
          <Typography fontSize="12.5px" color="text.secondary" fontWeight={500} mb={2}>
            Tag Name
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {allTags.map(tag => {
              const isAttached = attachedTagIds.has(tag.id)
              return (
                <Chip
                  key={tag.id}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {isAttached && <CheckIcon sx={{ fontSize: 12 }} />}
                      <span>{tag.name}</span>
                    </Box>
                  }
                  disabled={isAttached || isAdding}
                  onClick={() => !isAttached && !isAdding && onAdd(tag.id)}
                  sx={{
                    height: 32, fontSize: '12.5px', fontWeight: 600,
                    px: 0.5,
                    bgcolor: isAttached ? `${tag.color}15` : `${tag.color}18`,
                    color: isAttached ? `${tag.color}90` : tag.color,
                    border: `1.5px solid ${isAttached ? tag.color + '35' : tag.color + '55'}`,
                    cursor: isAttached ? 'default' : 'pointer',
                    transition: 'all 0.15s',
                    '&:hover': !isAttached && !isAdding ? {
                      bgcolor: `${tag.color}30`,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 3px 10px ${tag.color}30`,
                    } : {},
                    '&.Mui-disabled': {
                      opacity: 1,                         // keep colour visible
                      bgcolor: `${tag.color}10`,
                      color: `${tag.color}70`,
                      border: `1.5px solid ${tag.color}25`,
                    },
                  }}
                />
              )
            })}
          </Box>
        </Box>
      )}
    </EntityModal>
  )
}

// ── Dialogs ───────────────────────────────────────────────────────────────────

function AssignDialog({ open, lead, onClose, onSave, isSaving }: {
  open: boolean; lead: Lead; onClose: () => void; onSave: (id: string | null) => Promise<void>; isSaving: boolean
}) {
  const [assignedToId, setAssignedToId] = useState(lead.assignedTo?.id ?? '')

  useEffect(() => {
    if (open) {
      setAssignedToId(lead.assignedTo?.id ?? '')
    }
  }, [open, lead.assignedTo?.id])

  const { data: usersData, isLoading } = useQuery({ queryKey: ['users-list'], queryFn: () => usersAdminApi.getAll({ pageSize: 1000 }), enabled: open, staleTime: 60_000 })

  // Eligibility rules:
  //  • Must belong to the same Organization as the lead.
  //  • Org Admins (no specific LOB assigned — lobId is null) are eligible for any LOB in their org.
  //  • Normal users must belong to the exact same LOB as the lead.
  //  • SUPER_ADMIN accounts are never assignable (they have no org of their own).
  const users = (usersData?.items ?? []).filter(u => {
    if (u.roleName?.toUpperCase() === 'SUPER_ADMIN') return false
    if (lead.orgId && u.orgId !== lead.orgId) return false
    if (!u.lobId) return true // Org Admin — eligible across all LOBs in the org
    if (lead.lobId && u.lobId !== lead.lobId) return false
    return true
  })

  const options = useMemo(() => {
    if (isLoading || !usersData) {
      return [
        { value: '', label: 'Unassign', disabled: !lead.assignedTo },
        ...(lead.assignedTo ? [{ value: lead.assignedTo.id, label: lead.assignedTo.name }] : [])
      ]
    }

    return [
      { value: '', label: 'Unassign', disabled: !lead.assignedTo },
      ...users.map(u => ({
        value: u.id,
        label: u.name,
        disabled: u.id === lead.assignedTo?.id
      }))
    ]
  }, [isLoading, usersData, users, lead.assignedTo])

  const noEligibleUsers = !isLoading && !!usersData && users.length === 0

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      onSubmit={() => onSave(assignedToId || null)}
      title={lead.assignedTo ? 'Reassign Lead' : 'Assign Lead'}
      submitLabel={!assignedToId ? 'Unassign' : (lead.assignedTo ? 'Reassign' : 'Assign')}
      isSubmitting={isSaving}
      submitDisabled={assignedToId === (lead.assignedTo?.id ?? '')}
      icon={<AddIcon sx={{ fontSize: 18 }} />}
    >
      <FormSelect
        label="Assign To"
        value={assignedToId}
        onChange={(v: any) => setAssignedToId(v?.value ?? '')}
        options={options}
        fullWidth
      />
      {noEligibleUsers && (
        <Typography sx={{ fontSize: '12px', color: themeColors.error, mt: 1 }}>
          No eligible users found for {lead.lobName ?? 'this LOB'} in {lead.orgName ?? 'this organization'}.
          Only users in the same LOB, or Organization Admins, can be assigned.
        </Typography>
      )}
    </EntityModal>
  )
}


function AddActivityDialog({ open, defaultType, onClose, onSave, isSaving, performedById }: {
  open: boolean; defaultType?: string; onClose: () => void; onSave: (p: AddActivityPayload) => Promise<void>; isSaving: boolean; performedById: string
}) {
  const [type, setType] = useState<'CALL' | 'NOTE' | 'FOLLOW_UP'>('NOTE')
  const [desc, setDesc] = useState('')
  const [err, setErr] = useState('')

  useEffect(() => {
    if (open) {
      setType((defaultType as any) ?? 'NOTE')
      setDesc('')
      setErr('')
    }
  }, [open, defaultType])

  const LABELS = { CALL: 'Call Log', NOTE: 'Note / Comment', FOLLOW_UP: 'Follow-up' }
  return (
    <EntityModal
      open={open}
      onClose={onClose}
      onSubmit={async () => { if (!desc.trim()) { setErr('Required'); return } await onSave({ type, description: desc.trim(), performedById }) }}
      title="Log Activity"
      submitLabel="Add"
      isSubmitting={isSaving}
      icon={<SourceIcon sx={{ fontSize: 18 }} />}
    >
      <Stack spacing={2}>
        <FormSelect
          label="Type"
          value={type}
          onChange={(v: any) => setType(v?.value ?? 'NOTE')}
          options={MANUAL_ACTIVITY_TYPES.map(t => ({ value: t, label: LABELS[t as keyof typeof LABELS] }))}
          fullWidth
        />
        <FormInput label="Description *" value={desc} multiline rows={3}
          onChange={e => { setDesc(e.target.value); setErr('') }}
          error={!!err} helperText={err} fullWidth />
      </Stack>
    </EntityModal>
  )
}
// ── Status/Pipeline Progress Bar ──────────────────────────────────────────────

function LeadProgressBar({
  lead,
  onStatusClick,
  isChanging,
}: {
  lead: Lead
  onStatusClick?: (status: LeadStatus) => void
  isChanging?: boolean
}) {
  const activeStatuses: LeadStatus[] = ['NEW', 'CONTACTED', 'QUALIFIED', 'CONVERTED']
  const currentIdx = activeStatuses.indexOf(lead.status)

  const isConverted = lead.status === 'CONVERTED'
  const isJunk = lead.status === 'JUNK'

  const pct = isConverted
    ? 100
    : isJunk
      ? 0
      : currentIdx >= 0
        ? Math.round(((currentIdx + 1) / activeStatuses.length) * 100)
        : 0

  const canClickForward = !!onStatusClick && !isConverted && !isJunk

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
        <Typography fontSize="12px" fontWeight={600} color="text.secondary">
          Pipeline Progress
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {isChanging && (
            <CircularProgress size={10} sx={{ color: themeColors.primary }} />
          )}
          <Typography fontSize="12px" fontWeight={700} color={isJunk ? '#ef4444' : themeColors.primary}>
            {pct}%
          </Typography>
        </Box>
      </Box>

      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: '#e5e7eb',
          '& .MuiLinearProgress-bar': {
            borderRadius: 3,
            bgcolor: isJunk
              ? '#ef4444'
              : isConverted
                ? '#16a34a'
                : themeColors.primary,
            transition: 'width 0.4s ease',
          },
        }}
      />

      {canClickForward && (
        <Typography fontSize="10px" color="text.disabled" sx={{ mt: 0.75, mb: 0.25 }}>
          Click a stage to move the lead forward
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 0.5, mt: canClickForward ? 0.25 : 1.5, flexWrap: 'wrap' }}>
        {activeStatuses.map((s, i) => {
          const isCurrent = s === lead.status
          const isPast = !isJunk && !isConverted && currentIdx >= 0 && i < currentIdx
          const isFuture = !isJunk && !isConverted && currentIdx >= 0 && i > currentIdx
          // Only the immediate next stage is clickable (sequential progression)
          const isClickable = canClickForward && i === currentIdx + 1

          const stageColorHex = s === 'CONVERTED'
            ? '#16a34a'
            : s === 'QUALIFIED'
              ? '#9333ea'
              : s === 'CONTACTED'
                ? '#2563eb'
                : '#0f766e'

          return (
            <Tooltip
              key={s}
              title={
                isCurrent
                  ? `Current stage: ${STATUS_LABELS[s]}`
                  : isPast
                    ? `Completed: ${STATUS_LABELS[s]}`
                    : isClickable
                      ? `Move to "${STATUS_LABELS[s]}"`
                      : STATUS_LABELS[s]
              }
              arrow
            >
              <Box
                onClick={() => isClickable && onStatusClick?.(s)}
                sx={{
                  flex: 1,
                  minWidth: 50,
                  textAlign: 'center',
                  py: 0.5,
                  px: 0.5,
                  borderRadius: '4px',
                  position: 'relative',
                  // Current stage highlight
                  ...(isCurrent && {
                    cursor: 'default',
                    bgcolor: `${stageColorHex}20`,
                    border: `2px solid ${stageColorHex}`,
                    transform: 'scaleY(1.1)',
                    boxShadow: `0 2px 10px ${stageColorHex}50`,
                  }),
                  // Past stages
                  ...(isPast && {
                    cursor: 'not-allowed',
                    bgcolor: '#f0fdf4',
                    border: '1.5px solid #86efac',
                    opacity: 0.75,
                  }),
                  // Future stages
                  ...(isFuture && {
                    cursor: isClickable ? 'pointer' : 'default',
                    bgcolor: '#f8fafc',
                    border: '1.5px solid #e2e8f0',
                  }),
                  // Converted state styling
                  ...(isConverted && s === 'CONVERTED' && {
                    cursor: 'default',
                    bgcolor: '#e8f5e9',
                    border: '2px solid #16a34a',
                    boxShadow: '0 2px 10px rgba(22, 163, 74, 0.3)',
                  }),
                  ...(isConverted && s !== 'CONVERTED' && {
                    cursor: 'not-allowed',
                    bgcolor: '#f0fdf4',
                    border: '1.5px solid #86efac',
                    opacity: 0.75,
                  }),
                  // Junk state styling
                  ...(isJunk && {
                    cursor: 'not-allowed',
                    bgcolor: '#fef2f2',
                    border: '1.5px solid #fca5a5',
                    opacity: 0.6,
                  }),
                  transition: 'all 0.15s',
                  '&:hover': isClickable
                    ? {
                      bgcolor: `${stageColorHex}18`,
                      border: `1.5px solid ${stageColorHex}`,
                      transform: 'scaleY(1.05)',
                    }
                    : {},
                }}
              >
                {(isPast || (isConverted && s !== 'CONVERTED')) && (
                  <CheckIcon
                    sx={{
                      fontSize: 8,
                      color: '#16a34a',
                      position: 'absolute',
                      top: 2,
                      right: 3,
                    }}
                  />
                )}
                <Typography
                  fontSize="9px"
                  fontWeight={isCurrent || (isConverted && s === 'CONVERTED') ? 800 : isPast || (isConverted && s !== 'CONVERTED') ? 600 : 500}
                  sx={{
                    color: isCurrent
                      ? stageColorHex
                      : isConverted && s === 'CONVERTED'
                        ? '#16a34a'
                        : isPast || (isConverted && s !== 'CONVERTED')
                          ? '#16a34a'
                          : isJunk
                            ? '#dc2626'
                            : '#94a3b8',
                  }}
                  noWrap
                >
                  {STATUS_LABELS[s]}
                </Typography>
              </Box>
            </Tooltip>
          )
        })}
      </Box>
    </Box>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const LeadDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const isFromSales = window.location.pathname.startsWith('/sales')
  const backPath = isFromSales ? '/sales' : '/leads'

  // ── Permission flags
  const { hasAction, isSuperAdmin } = usePermission()
  const userId = authStore.user?.userId ?? ''
  const canUpdate = hasAction('/leads', 'update')
  const canUpdateOwn = hasAction('/leads', 'update_own')
  const canUpdateIntel = hasAction('/leads', 'upload_intel')
  const canDelete = hasAction('/leads', 'delete')
  const canAssign = hasAction('/leads', 'assign')
  const canConvert = hasAction('/leads', 'convert')
  // ── Granular sub-section actions (role_menu_action schema) ──
  const canChangeStatus = hasAction('/leads', 'change_status')
  const canCallLog = hasAction('/leads', 'call_log')
  const canComposeMail = hasAction('/leads', 'compose_mail')
  const canAddComment = hasAction('/leads', 'add_comment')
  const canAddTask = hasAction('/leads', 'add_task')
  const canAddEvent = hasAction('/leads', 'add_event')
  const canAddAttachment = hasAction('/leads', 'add_attachment')
  const canUpdateRequirements = hasAction('/leads', 'update_requirements') || canUpdate || canUpdateOwn
  const canUpdateBestTime = hasAction('/leads', 'update_besttimetocall') || hasAction('/leads', 'update_best_time_to_call') || canUpdate || canUpdateOwn
  // ── Tag-specific action: create_tag controls whether the user can create a new tag ──
  // Checked against /leads since the API returns create_tag as an action on the
  // Leads Management menu — this correctly respects the per-role isChecked value.
  // Tag add/remove on a lead (assigning existing tags) is already gated by canModify (update).
  const canCreateTag = hasAction('/leads', 'create_tag')

  // ── Dialog / UI state
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [isJunkConfirmOpen, setIsJunkConfirmOpen] = useState(false)
  const [emailOpen, setEmailOpen] = useState(false)

  const sendEmailMutation = useMutation({
    mutationFn: (data: SendEmailData) =>
      emailApi.send({
        entityType: 'LEAD',
        entityId: lead?.id,
        to: data.to,
        cc: data.cc.length > 0 ? data.cc : undefined,
        bcc: data.bcc.length > 0 ? data.bcc : undefined,
        subject: data.subject,
        htmlBody: data.body,
        files: data.attachments.length > 0 ? data.attachments : undefined,
      }),
    onSuccess: () => {
      toast.success('Email sent successfully')
      setEmailOpen(false)
      qc.invalidateQueries({ queryKey: ['emails', 'LEAD', lead?.id] })
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Failed to send email')
    },
  })
  const [headerCreateEventOpen, setHeaderCreateEventOpen] = useState(false)
  const [assignOpen, setAssignOpen] = useState(false)
  const [convertOpen, setConvertOpen] = useState(false)
  const [activityOpen, setActivityOpen] = useState(false)
  const [activityType, setActivityType] = useState<string>('NOTE')
  const tabParam = new URLSearchParams(window.location.search).get('tab')
  const initialTab = tabParam === 'tasks' ? 4 : 0
  const [activeTab, setActiveTab] = useState(initialTab)
  const [tagModalOpen, setTagModalOpen] = useState(false)
  // Inline description edit
  const [editingDesc, setEditingDesc] = useState(false)
  const [descVal, setDescVal] = useState('')

  const saveDesc = async () => {
    if (descVal === lead.nextAction) { setEditingDesc(false); return }
    await updateMut.mutateAsync({ nextAction: descVal })
    setEditingDesc(false)
  }

  const [editingBestTime, setEditingBestTime] = useState(false)
  const [bestTimeVal, setBestTimeVal] = useState('')


  const [editingTaxInfo, setEditingTaxInfo] = useState(false)
  const [taxVals, setTaxVals] = useState({ address: '', gst: '', pincode: '' })
  const [taxErrors, setTaxErrors] = useState<{ gst?: string; pincode?: string }>({})

  const [editingProfInfo, setEditingProfInfo] = useState(false)
  const [profVals, setProfVals] = useState({ designation: '', websiteUrl: '', industryType: '', customerTypeId: null as number | null, fileType: '' })

  const { data: customerTypes = [] } = useQuery<CustomerType[]>({
    queryKey: ['customer-types'],
    queryFn: getCustomerTypes,
    staleTime: 5 * 60 * 1000,
  })


  // ── Queries
  const { data: lead, isLoading, isError } = useQuery({ queryKey: ['lead', id], queryFn: () => leadsApi.getById(id!), enabled: !!id })
  const { data: activities = [], isLoading: loadingActs } = useQuery({ queryKey: ['lead-activities', id], queryFn: () => leadsApi.getActivities(id!), enabled: !!id })
  const { data: allTags = [] } = useQuery<Tag[]>({
    queryKey: ['tags', lead?.orgId],
    queryFn: () => tagsApi.getAll(lead?.orgId ? { orgId: lead.orgId } : undefined),
    staleTime: 60_000,
    enabled: !!lead,
  })
  const { data: tasks = [] } = useQuery({ queryKey: ['lead-tasks', id], queryFn: () => leadTasksApi.getAll(id!), enabled: !!id })
  const { data: events = [] } = useQuery({ queryKey: ['lead-follow-ups', id], queryFn: () => leadFollowUpsApi.getAll(id!), enabled: !!id })

  const { data: notes = [], isLoading: loadingNotes } = useQuery({
    queryKey: ['lead-notes', id],
    queryFn: () => leadNotesApi.getAll(id!),
    enabled: !!id
  })

  // Global calling store usage

  // ── Mutations
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['lead', id] })
    qc.invalidateQueries({ queryKey: ['lead-activities', id] })
    qc.invalidateQueries({ queryKey: ['lead-notes', id] })
    qc.invalidateQueries({ queryKey: ['lead-tasks', id] })
    qc.invalidateQueries({ queryKey: ['lead-follow-ups', id] })
    qc.invalidateQueries({ queryKey: ['calendar-lead-tasks'] })
    qc.invalidateQueries({ queryKey: ['all-follow-ups'] })
    qc.invalidateQueries({ queryKey: ['leads-list'] })
    qc.invalidateQueries({ queryKey: ['deals-list'] })
    qc.invalidateQueries({ queryKey: ['deals-open'] })
    qc.invalidateQueries({ queryKey: ['deals-all-summary'] })
  }

  const updateMut = useMutation({ mutationFn: (p: UpdateLeadPayload) => leadsApi.update(id!, p), onSuccess: () => { toast.success('Lead updated'); invalidate(); setEditOpen(false); setEditingProfInfo(false) }, onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Failed to update') })
  const updateReqsMut = useMutation({
    mutationFn: (reqs: string | null) => leadsApi.updateRequirements(id!, reqs),
    onSuccess: () => {
      toast.success('Requirements updated')
      invalidate()
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Failed to update requirements')
  })
  const deleteMut = useMutation({ mutationFn: () => leadsApi.delete(id!), onSuccess: () => { toast.success('Lead deleted'); navigate(backPath) }, onError: () => toast.error('Failed to delete') })
  const statusMut = useMutation({
    mutationFn: (s: LeadStatus) => leadsApi.changeStatus(id!, s),
    onSuccess: () => {
      toast.success('Status updated')
      invalidate()
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Invalid transition')
  })
  const assignMut = useMutation({ mutationFn: (aid: string | null) => leadsApi.assign(id!, aid), onSuccess: () => { toast.success('Assignment updated'); invalidate(); setAssignOpen(false) }, onError: () => toast.error('Failed to assign') })

  const activityMut = useMutation({ mutationFn: (p: AddActivityPayload) => leadsApi.addActivity(id!, p), onSuccess: () => { toast.success('Activity logged'); qc.invalidateQueries({ queryKey: ['lead-activities', id] }); setActivityOpen(true) }, onError: () => toast.error('Failed') })
  const addTagMut = useMutation({ mutationFn: (tid: string) => leadsApi.addTag(id!, tid), onSuccess: () => { toast.success('Tag added'); invalidate() }, onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed') })
  const removeTagMut = useMutation({ mutationFn: (tid: string) => leadsApi.removeTag(id!, tid), onSuccess: () => { toast.success('Tag removed'); invalidate() }, onError: () => toast.error('Failed') })

  // ── Notes Mutations ──
  const addNoteMut = useMutation({
    mutationFn: (content: string) => leadNotesApi.create(id!, content),
    onSuccess: () => { invalidate(); toast.success('Note added') }
  })
  const updateNoteMut = useMutation({
    mutationFn: ({ noteId, content }: { noteId: string; content: string }) => leadNotesApi.update(id!, noteId, content),
    onSuccess: () => { invalidate(); toast.success('Note updated') }
  })
  const deleteNoteMut = useMutation({
    mutationFn: (noteId: string) => leadNotesApi.delete(id!, noteId),
    onSuccess: () => { invalidate(); toast.success('Note deleted') }
  })

  // ── Loading / error states
  if (isLoading) return <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}><CircularProgress /></Box>
  if (isError || !lead) return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography color="error">Lead not found.</Typography>
      <Button onClick={() => navigate('/leads')} sx={{ mt: 2 }} startIcon={<BackIcon />}>Back</Button>
    </Box>
  )

  // ── Derived flags
  const isConverted = lead.status === 'CONVERTED'
  const pendingTasksCount = tasks.filter(t => t.status === 'PENDING' || t.status === 'IN_PROGRESS').length
  const pendingEventsCount = events.filter(f => f.status === 'PENDING').length
  const hasPendingItems = pendingTasksCount > 0 || pendingEventsCount > 0
  const isOwnerOrAssignee = lead.owner.id === userId || lead.assignedTo?.id === userId
  const canModify = isSuperAdmin || canUpdate || (canUpdateOwn && isOwnerOrAssignee)
  const canModifyIntel = canModify || (canUpdateIntel && isOwnerOrAssignee)
  const canDel = isSuperAdmin || lead.owner.id === userId || canDelete
  const attachedTagIds = new Set(lead.tags.map(t => t.id))
  const color = avatarColor(lead.name)

  const openActivity = (type: string) => { setActivityType(type); setActivityOpen(true) }

  // Tab labels
  const TABS = ['Activity', 'Call', 'Email', 'Comments', 'Tasks', 'Events', 'Attachments']

  const leadTabs = [
    {
      label: 'Activity',
      content: (
        <Box>
          <UniversalActivityTimeline logs={activities} isLoading={loadingActs} entityType="lead" />
        </Box>
      ),
    },
    {
      label: 'Call',
      content: (
        <Box>
          {canCallLog && !isConverted && lead.status !== 'JUNK' && (<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button size="small" variant="contained" startIcon={<PhoneIcon sx={{ fontSize: '13px !important' }} />}
              disabled={callStore.isStarting || isConverted}
              onClick={() => callStore.showPlaceCall({
                entityType: 'LEAD',
                entityId: lead.id,
                name: lead.name,
                phone: lead.phone,
                companyName: lead.companyName ?? undefined
              })}
              sx={{
                borderRadius: '8px', textTransform: 'none', fontSize: '12px', fontWeight: 600, background: '#7b1fa2',
                '&:hover': { background: '#6a1b9a' }
              }}>
              {callStore.isStarting ? <CircularProgress size={13} sx={{ color: '#fff', mr: 0.5 }} /> : null}
              Log Call
            </Button>
          </Box>
          )}
          <CallHistorySection leadId={lead.id} />
        </Box>
      ),
    },
    {
      label: 'Email',
      content: (
        <EmailHistorySection
          entityType="LEAD"
          entityId={lead.id}
          canModify={canComposeMail && !isConverted && lead.status !== 'JUNK'}
          onCompose={() => {
            if (!lead.email) { toast.error('No email address found for this lead'); return }
            setEmailOpen(true)
          }}
        />
      ),
    },
    {
      label: 'Comments',
      content: (
        <Box>
          {canAddComment && !isConverted && lead.status !== 'JUNK' && <CommentInput onAdd={async (c) => { await addNoteMut.mutateAsync(c) }} isAdding={addNoteMut.isPending} />}
          {loadingNotes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          ) : notes.length === 0 ? (
            <CommentsEmptyState />
          ) : (
            <Stack spacing={0}>
              {notes.map((note, index) => (
                <CommentItem
                  key={note.id}
                  note={note}
                  onUpdate={lead.status !== 'JUNK' ? async (c) => { await updateNoteMut.mutateAsync({ noteId: note.id, content: c }) } : undefined}
                  onDelete={lead.status !== 'JUNK' ? () => deleteNoteMut.mutate(note.id) : undefined}
                  isUpdating={updateNoteMut.isPending}
                  isLast={index === notes.length - 1}
                />
              ))}
            </Stack>
          )}
        </Box>
      ),
    },
    {
      label: 'Tasks',
      content: <LeadTasksSection leadId={lead.id} canModify={canAddTask && !isConverted && lead.status !== 'JUNK'} />,
    },
    {
      label: 'Events',
      content: (
        <LeadEventsSection
          leadId={lead.id}
          canModify={canAddEvent && !isConverted && lead.status !== 'JUNK'}
          externalCreateOpen={headerCreateEventOpen}
          onExternalCreateClose={() => setHeaderCreateEventOpen(false)}
        />
      ),
    },
    {
      label: 'Attachments',
      content: <AttachmentsSection moduleType="lead" recordId={lead.id} canModify={canAddAttachment && !isConverted && lead.status !== 'JUNK'} />,
    },
  ]

  return (
    <Box sx={{
      height: '100%', overflowY: 'auto', bgcolor: '#f5f6fa',
      '&::-webkit-scrollbar': { width: '5px' }, '&::-webkit-scrollbar-thumb': { bgcolor: '#d1d5db', borderRadius: '4px' }
    }}>

      {/* ── Breadcrumb ── */}
      <Box sx={{ px: 3, pt: 2, pb: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton size="small" onClick={() => navigate(backPath)} sx={{ color: '#6b7280', mr: 0.5 }}>
          <BackIcon fontSize="small" />
        </IconButton>
        <Typography fontSize="13px" color="text.secondary" sx={{ cursor: 'pointer', '&:hover': { color: 'text.primary' } }}
          onClick={() => navigate(backPath)}>
          {isFromSales ? 'Sales' : 'Leads'}
        </Typography>
        <Typography fontSize="13px" color="text.secondary" sx={{ mx: 0.25 }}>/</Typography>
        <Typography fontSize="13px" fontWeight={600} color="text.primary">{lead.name}</Typography>
        <Box sx={{ flex: 1 }} />
        {/* Top-right quick actions */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {canModify && !isConverted && lead.status !== 'JUNK' && (
            <Tooltip title="Edit lead">
              <IconButton size="small" onClick={() => setEditOpen(true)} sx={{ color: '#6b7280' }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canDel && (
            <Tooltip title="Delete lead">
              <IconButton size="small" onClick={() => setDeleteOpen(true)} sx={{ color: '#ef4444' }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* ── Hero Card ── */}
      <Box sx={{ px: 3, pt: 1.5, pb: 0 }}>
        <Paper elevation={0} sx={{
          border: `2px solid ${themeColors.primary}45`,
          borderRadius: '14px', p: 2.5, bgcolor: '#fff',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>

            {/* Avatar */}
            <Avatar sx={{
              width: 62, height: 62, bgcolor: color, fontSize: '22px', fontWeight: 700,
              flexShrink: 0, boxShadow: `0 4px 12px ${color}50`
            }}>
              {getInitials(lead.name)}
            </Avatar>

            {/* Name + meta */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                <Typography fontWeight={700} fontSize="18px" color="text.primary">{lead.name}</Typography>
                <StatusBadge status={lead.status} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <BusinessIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                  <Typography fontSize="12.5px" color="text.secondary">
                    {lead.companyName || lead.orgName || SOURCE_LABELS[lead.source] || '—'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <DateIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                  <Typography fontSize="12.5px" color="text.secondary">
                    Lead Created: {formatDate(lead.createdAt)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <DateIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                  <Typography fontSize="12.5px" color="text.secondary">
                    Expected Closing Date: {formatDate(lead.expectedCloseDate)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography fontSize="12.5px" color="text.secondary" fontWeight={600}>
                    Expected Order Value: {lead.expectedAmount != null ? `₹${lead.expectedAmount.toLocaleString('en-IN')}` : '—'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', flexShrink: 0 }}>

              {!isConverted && lead.status !== 'JUNK' && (<>
                {canCallLog && (
                  <Button size="small" variant="outlined" startIcon={<PhoneIcon sx={{ fontSize: '14px !important' }} />}
                    disabled={callStore.isStarting}
                    onClick={() => callStore.showPlaceCall({
                      entityType: 'LEAD',
                      entityId: lead.id,
                      name: lead.name,
                      phone: lead.phone,
                      companyName: lead.companyName ?? undefined
                    })}
                    sx={{
                      borderRadius: '8px', textTransform: 'none', fontSize: '12.5px', fontWeight: 600, color: '#374151',
                      borderColor: '#d1d5db', '&:hover': { borderColor: themeColors.primary, color: themeColors.primary }
                    }}>
                    {callStore.isStarting ? <CircularProgress size={13} sx={{ mr: 0.5 }} /> : null}
                    Call
                  </Button>
                )}
                {canComposeMail && (
                  <Button size="small" variant="outlined" startIcon={<EmailIcon sx={{ fontSize: '14px !important' }} />}
                    onClick={() => {
                      if (!lead.email) { toast.error('No email address found for this lead'); return }
                      setEmailOpen(true)
                    }}
                    sx={{
                      borderRadius: '8px', textTransform: 'none', fontSize: '12.5px', fontWeight: 600, color: '#374151',
                      borderColor: '#d1d5db', '&:hover': { borderColor: themeColors.primary, color: themeColors.primary }
                    }}>
                    Email
                  </Button>
                )}
                {canAddComment && (
                  <Button size="small" variant="outlined" startIcon={<CommentIcon sx={{ fontSize: '14px !important' }} />}
                    onClick={() => setActiveTab(3)}
                    sx={{
                      borderRadius: '8px', textTransform: 'none', fontSize: '12.5px', fontWeight: 600, color: '#374151',
                      borderColor: '#d1d5db', '&:hover': { borderColor: themeColors.primary, color: themeColors.primary }
                    }}>
                    Comment
                  </Button>
                )}
              </>
              )}
              {!isConverted && lead.status !== 'JUNK' && (
                <>
                  {canAddTask && (
                    <Button size="small" variant="outlined" startIcon={<AddIcon sx={{ fontSize: '14px !important' }} />}
                      onClick={() => setActiveTab(4)}
                      sx={{
                        borderRadius: '8px', textTransform: 'none', fontSize: '12.5px', fontWeight: 600, color: '#374151',
                        borderColor: '#d1d5db', '&:hover': { borderColor: themeColors.primary, color: themeColors.primary }
                      }}>
                      Add Task
                    </Button>
                  )}
                  {canAddEvent && (
                    <Button size="small" variant="outlined" startIcon={<CalendarIcon sx={{ fontSize: '14px !important' }} />}
                      onClick={() => {
                        setActiveTab(5)
                        setHeaderCreateEventOpen(true)
                      }}
                      sx={{
                        borderRadius: '8px', textTransform: 'none', fontSize: '12.5px', fontWeight: 600, color: '#374151',
                        borderColor: '#d1d5db', '&:hover': { borderColor: themeColors.primary, color: themeColors.primary }
                      }}>
                      Create Event
                    </Button>
                  )}
                </>
              )}
              {canChangeStatus && !isConverted && lead.status !== 'JUNK' && (
                <Button size="small" variant="outlined" color="error"
                  disabled={statusMut.isPending}
                  onClick={() => setIsJunkConfirmOpen(true)}
                  sx={{
                    borderRadius: '8px', textTransform: 'none', fontSize: '12.5px', fontWeight: 600,
                  }}>
                  Mark as Junk
                </Button>
              )}
              {canConvert && !isConverted && lead.status !== 'JUNK' && (
                <Tooltip title={lead.status !== 'QUALIFIED' ? "Leads can only be converted to deals when status is Qualified" : ""}>
                  <span>
                    <Button variant="contained" size="small" endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                      disabled={lead.status !== 'QUALIFIED'}
                      onClick={() => setConvertOpen(true)}
                      sx={{
                        borderRadius: '8px', textTransform: 'none', fontSize: '12.5px', fontWeight: 700,
                        background: themeColors.primary,
                        '&:hover': { background: themeColors.primaryDark }
                      }}>
                      Convert to Deal
                    </Button>
                  </span>
                </Tooltip>
              )}
              {isConverted && (lead.convertedToDealId || lead.convertedToCustomerId) && (
                <Button size="small" variant="outlined" endIcon={<OpenInNewIcon sx={{ fontSize: '13px !important' }} />}
                  onClick={() => {
                    if (lead.convertedToDealId) {
                      navigate(`/deals/${lead.convertedToDealId}`)
                    } else {
                      navigate(`/customers/${lead.convertedToCustomerId}`)
                    }
                  }}
                  sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '12.5px', fontWeight: 600, color: '#16a34a', borderColor: '#16a34a40' }}>
                  {lead.convertedToDealId ? "View Deal" : "View Customer"}
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* ── Two-column body ── */}
      <Box sx={{ display: 'flex', gap: 2.5, p: 3, pt: 2, alignItems: 'flex-start' }}>

        {/* ════ Left Sidebar ════ */}
        <Box sx={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Status */}
          <SideCard title='Pipeline Status'>
            <LeadProgressBar
              lead={lead}
              isChanging={statusMut.isPending}
              onStatusClick={canChangeStatus && !isConverted && lead.status !== 'JUNK' ? (targetStatus) => {
                if (targetStatus === 'CONVERTED') {
                  setConvertOpen(true)
                } else if (targetStatus === 'JUNK') {
                  setIsJunkConfirmOpen(true)
                } else {
                  statusMut.mutate(targetStatus)
                }
              } : undefined}
            />
          </SideCard>

          {/* Basic Info */}
          <SideCard title='Basic Info'>
            <Stack spacing={0.5}>
              <InfoRow icon={<EmailIcon sx={{ fontSize: 15 }} />} label="Email"
                value={<Typography fontSize="12.5px" noWrap sx={{ color: lead.email ? 'text.primary' : '#9ca3af' }}>{lead.email || 'No email'}</Typography>} />
              <InfoRow icon={<PhoneIcon sx={{ fontSize: 15 }} />} label="Phone"
                value={<Typography fontSize="12.5px">{maskPhoneNumber(lead.phone)}</Typography>} />
              <InfoRow icon={<BusinessIcon sx={{ fontSize: 15 }} />} label="Company"
                value={
                  lead.convertedToCustomerId ? (
                    <Box
                      onClick={() => navigate(`/customers/${lead.convertedToCustomerId}`)}
                      sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                        color: themeColors.primary, cursor: 'pointer',
                        '&:hover .company-text': { textDecoration: 'underline' },
                        '&:hover .link-icon': { transform: 'translateX(2px)' },
                      }}
                    >
                      <Typography className="company-text" fontSize="12.5px" fontWeight={600}>
                        {lead.companyName || lead.orgName || 'View Company'}
                      </Typography>
                      <ArrowForwardIcon className="link-icon" sx={{ fontSize: 13, transition: 'transform 0.2s' }} />
                    </Box>
                  ) : (
                    <Typography fontSize="12.5px" sx={{ color: (lead.companyName || lead.orgName) ? 'text.primary' : '#9ca3af' }}>
                      {lead.companyName || lead.orgName || '—'}
                    </Typography>
                  )
                }
              />
              <InfoRow icon={<SourceIcon sx={{ fontSize: 15 }} />} label="Source"
                value={<Typography fontSize="12.5px">{SOURCE_LABELS[lead.source] || lead.source}</Typography>} />
            </Stack>
          </SideCard>

          {/* Best Time to Contact */}
          <SideCard title="Best Time to Contact" action={
            canUpdateBestTime && !isConverted && lead.status !== 'JUNK' && !editingBestTime && (
              <IconButton size="small" onClick={() => {
                setBestTimeVal(lead.bestTimeToContact || '')
                setEditingBestTime(true)
              }} sx={{ color: '#9ca3af', p: 0.25, '&:hover': { color: themeColors.primary } }}>
                <EditIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )
          }>
            {editingBestTime ? (
              <Box>
                <FormInput
                  label="Best Time to Contact"
                  value={bestTimeVal}
                  onChange={e => setBestTimeVal(e.target.value)}
                  placeholder="e.g. Weekdays 10 AM - 12 PM"
                  fullWidth
                  sx={{ mb: 1.5 }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="contained" startIcon={<CheckIcon />}
                    disabled={updateMut.isPending}
                    onClick={() => {
                      updateMut.mutate({
                        bestTimeToContact: bestTimeVal.trim() || null
                      }, {
                        onSuccess: () => setEditingBestTime(false)
                      })
                    }}
                    sx={{ fontSize: '11.5px', textTransform: 'none', background: themeColors.primary }}>
                    Save
                  </Button>
                  <Button size="small" variant="outlined"
                    onClick={() => setEditingBestTime(false)}
                    sx={{ fontSize: '11.5px', textTransform: 'none', color: '#6b7280', borderColor: '#d1d5db' }}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              <InfoRow icon={<AccessTimeIcon sx={{ fontSize: 15 }} />} label="Preferred Time"
                value={<Typography fontSize="12.5px" sx={{ color: lead.bestTimeToContact ? 'text.primary' : '#9ca3af' }}>{lead.bestTimeToContact || 'Not specified'}</Typography>} />
            )}
          </SideCard>

          {/* Requirements */}
          <RequirementsSideCard
            value={lead.requirements}
            onSave={(reqs) => updateReqsMut.mutateAsync(reqs)}
            isPending={updateReqsMut.isPending}
            canEdit={canUpdateRequirements && !isConverted && lead.status !== 'JUNK'}
          />

          {/* Professional Info */}
          <SideCard title="Professional Info" action={
            canModifyIntel && !isConverted && lead.status !== 'JUNK' && !editingProfInfo && (
              <IconButton size="small" onClick={() => {
                setProfVals({
                  designation: lead.designation || '',
                  websiteUrl: lead.websiteUrl || '',
                  industryType: lead.industryType || '',
                  customerTypeId: lead.customerTypeId ?? null,
                  fileType: lead.fileType || '',
                })
                setEditingProfInfo(true)
              }} sx={{ color: '#9ca3af', p: 0.25, '&:hover': { color: themeColors.primary } }}>
                <EditIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )
          }>
            {editingProfInfo ? (
              <Box>
                {/* Customer Type */}
                <Box sx={{ mb: 1.5 }}>
                  <Typography fontSize="12px" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>Customer Type</Typography>
                  <select
                    value={profVals.customerTypeId ?? ''}
                    onChange={e => setProfVals(prev => ({
                      ...prev,
                      customerTypeId: e.target.value ? Number(e.target.value) : null,
                    }))}
                    style={{
                      width: '100%', padding: '8px 10px', fontSize: '13px',
                      border: '1px solid #e5e7eb', borderRadius: '6px',
                      background: '#fff', color: profVals.customerTypeId ? '#111827' : '#9ca3af',
                      outline: 'none', cursor: 'pointer',
                    }}
                  >
                    <option value="">Select Customer Type</option>
                    {customerTypes.map(ct => <option key={ct.id} value={ct.id}>{ct.name}</option>)}
                  </select>
                </Box>
                <FormInput
                  label="Designation"
                  value={profVals.designation}
                  onChange={e => setProfVals(prev => ({ ...prev, designation: e.target.value }))}
                  placeholder="e.g. Manager, Director"
                  fullWidth sx={{ mb: 1.5 }}
                />
                <FormInput
                  label="Website URL"
                  value={profVals.websiteUrl}
                  onChange={e => setProfVals(prev => ({ ...prev, websiteUrl: e.target.value }))}
                  placeholder="e.g. https://example.com"
                  fullWidth sx={{ mb: 1.5 }}
                />
                <FormInput
                  label="File Type (URL)"
                  value={profVals.fileType}
                  onChange={e => setProfVals(prev => ({ ...prev, fileType: e.target.value }))}
                  placeholder="e.g. https://example.com/file"
                  fullWidth sx={{ mb: 1.5 }}
                />
                <Box sx={{ mb: 1.5 }}>
                  <Typography fontSize="12px" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>Industry</Typography>
                  <select
                    value={profVals.industryType}
                    onChange={e => setProfVals(prev => ({ ...prev, industryType: e.target.value }))}
                    style={{
                      width: '100%', padding: '8px 10px', fontSize: '13px',
                      border: '1px solid #e5e7eb', borderRadius: '6px',
                      background: '#fff', color: profVals.industryType ? '#111827' : '#9ca3af',
                      outline: 'none', cursor: 'pointer',
                    }}
                  >
                    <option value="">Select industry</option>
                    {INDUSTRY_TYPES.map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="contained" startIcon={<CheckIcon />}
                    disabled={updateMut.isPending}
                    onClick={() => {
                      updateMut.mutate({
                        designation: profVals.designation.trim(),
                        websiteUrl: profVals.websiteUrl.trim(),
                        industryType: profVals.industryType.trim(),
                        // 0 = clear; positive = set; null = unchanged (send current value)
                        customerTypeId: profVals.customerTypeId ?? 0,
                        fileType: profVals.fileType.trim() || null,
                      })
                    }}
                    sx={{ borderRadius: '6px' }}>Save</Button>
                  <Button size="small" onClick={() => setEditingProfInfo(false)}>Cancel</Button>
                </Box>
              </Box>
            ) : (
              <Stack spacing={0.5}>
                <InfoRow icon={<IndustryIcon sx={{ fontSize: 15 }} />} label="Customer Type"
                  value={<Typography fontSize="12.5px" sx={{ color: lead.customerTypeName ? 'text.primary' : '#9ca3af' }}>{lead.customerTypeName || '—'}</Typography>} />
                <InfoRow icon={<WorkIcon sx={{ fontSize: 15 }} />} label="Designation"
                  value={<Typography fontSize="12.5px" sx={{ color: lead.designation ? 'text.primary' : '#9ca3af' }}>{lead.designation || '—'}</Typography>} />
                <InfoRow icon={<IndustryIcon sx={{ fontSize: 15 }} />} label="Industry"
                  value={<Typography fontSize="12.5px" sx={{ color: lead.industryType ? 'text.primary' : '#9ca3af' }}>{lead.industryType || '—'}</Typography>} />
                <InfoRow icon={<LinkIcon sx={{ fontSize: 15 }} />} label="Website"
                  value={
                    lead.websiteUrl ? (
                      <Box
                        component="a"
                        href={lead.websiteUrl.startsWith('http') ? lead.websiteUrl : `https://${lead.websiteUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: themeColors.primary, textDecoration: 'none', fontSize: '12.5px', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {lead.websiteUrl}
                        <OpenInNewIcon sx={{ fontSize: 11 }} />
                      </Box>
                    ) : (
                      <Typography fontSize="12.5px" sx={{ color: '#9ca3af' }}>—</Typography>
                    )
                  }
                />
                <InfoRow icon={<FileIcon sx={{ fontSize: 15 }} />} label="File Type"
                  value={
                    lead.fileType ? (
                      <Box
                        component="a"
                        href={lead.fileType.startsWith('http') ? lead.fileType : `https://${lead.fileType}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: themeColors.primary, textDecoration: 'none', fontSize: '12.5px', '&:hover': { textDecoration: 'underline' } }}
                      >
                        <span style={{ maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {lead.fileType}
                        </span>
                        <OpenInNewIcon sx={{ fontSize: 11 }} />
                      </Box>
                    ) : (
                      <Typography fontSize="12.5px" sx={{ color: '#9ca3af' }}>—</Typography>
                    )
                  }
                />
              </Stack>
            )}
          </SideCard>

          <AddressAndTaxInfoCard
            address={lead.address}
            gst={lead.gst}
            pincode={lead.pincode}
            canEdit={canModify && !isConverted && lead.status !== 'JUNK'}
            isSaving={updateMut.isPending}
            onSave={(data) => updateMut.mutate(data)}
          />

          {/* Description */}
          <SideCard title='Description' action={
            canModifyIntel && !isConverted && lead.status !== 'JUNK' && !editingDesc && (
              <IconButton size="small" onClick={() => { setDescVal(lead.nextAction ?? ''); setEditingDesc(true) }}
                sx={{ color: '#9ca3af', p: 0.25, '&:hover': { color: themeColors.primary } }}>
                <EditIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )
          }>
            {editingDesc ? (
              <Box>
                <FormInput value={descVal}
                  onChange={e => setDescVal(e.target.value)} autoFocus multiline rows={3}
                  fullWidth />
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button size="small" variant="contained" onClick={saveDesc} disabled={updateMut.isPending}
                    sx={{ borderRadius: '6px', fontSize: '11px', textTransform: 'none', py: 0.25, background: themeColors.primary, '&:hover': { background: themeColors.primaryDark } }}>
                    {updateMut.isPending ? <CircularProgress size={12} sx={{ color: '#fff' }} /> : 'Save'}
                  </Button>
                  <Button size="small" onClick={() => setEditingDesc(false)}
                    sx={{ borderRadius: '6px', fontSize: '11px', textTransform: 'none', py: 0.25 }}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              <Typography fontSize="12.5px" color={lead.nextAction ? 'text.primary' : 'text.secondary'}
                sx={{ lineHeight: 1.65, fontStyle: lead.nextAction ? 'normal' : 'italic' }}>
                {lead.nextAction || 'No description added yet.'}
              </Typography>
            )}
          </SideCard>

          {/* Lead Owner & Assignment */}
          <SideCard title="Ownership">
            <Box sx={{ px: 0.75, py: 0.5 }}>
              <Typography fontSize="11px" color="text.secondary" mb={0.75} fontWeight={500}>Lead Owner</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{
                  width: 32, height: 32, fontSize: '12px', fontWeight: 700,
                  bgcolor: avatarColor(lead.owner.name), flexShrink: 0
                }}>
                  {getInitials(lead.owner.name)}
                </Avatar>
                <Typography fontSize="13px" fontWeight={500}>{lead.owner.name}</Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #f0f0f0' }}>
              <Typography fontSize="11px" color="text.secondary" mb={0.75} fontWeight={500}>Assigned To</Typography>
              <Tooltip title={canAssign && !isConverted && lead.status !== 'JUNK' ? 'Click to reassign' : ''}>
                <Box onClick={() => canAssign && !isConverted && lead.status !== 'JUNK' && setAssignOpen(true)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, px: 0.75, py: 0.5, borderRadius: '8px',
                    cursor: canAssign && !isConverted && lead.status !== 'JUNK' ? 'pointer' : 'default',
                    '&:hover': canAssign && !isConverted && lead.status !== 'JUNK' ? { bgcolor: '#f5f7fb' } : {}
                  }}>
                  <Avatar sx={{
                    width: 28, height: 28, fontSize: '10px', fontWeight: 700,
                    bgcolor: lead.assignedTo ? '#7b1fa2' : '#9ca3af', flexShrink: 0
                  }}>
                    {lead.assignedTo ? getInitials(lead.assignedTo.name) : '?'}
                  </Avatar>
                  <Typography fontSize="12.5px" sx={{ flex: 1 }}>
                    {lead.assignedTo ? lead.assignedTo.name : 'Not assigned'}
                  </Typography>
                  {canAssign && !isConverted && <ArrowDownIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
                </Box>
              </Tooltip>
            </Box>
            <HelpAssigneeSelector
              currentPath="/leads"
              lobId={lead?.lobId}
              currentHelpAssigneeId={lead?.helpAssignee?.id}
              currentHelpAssigneeName={lead?.helpAssignee?.name}
              disabled={isConverted}
              onUpdate={async (newId) => {
                await leadsApi.updateHelpAssignee(lead.id, newId)
                qc.invalidateQueries({ queryKey: ['lead', id] })
                qc.invalidateQueries({ queryKey: ['lead-activities', id] })
              }}
            />
          </SideCard>

          {/* Business / LOB / Warehouse */}
          {(lead.orgName || lead.lobName) && (
            <SideCard>
              {lead.orgName && <SummaryRow label="Business" value={lead.orgName} />}
              {lead.lobName && <SummaryRow label="LOB" value={lead.lobName} />}
            </SideCard>
          )}

          {/* Tags */}
          <SideCard>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography fontWeight={700} fontSize="13px">Tags</Typography>
              {canModify && !isConverted && lead.status !== 'JUNK' && allTags.length > 0 && (
                <Tooltip title="Add tag">
                  <IconButton size="small" onClick={() => setTagModalOpen(true)}
                    sx={{
                      width: 26, height: 26, border: `1px solid ${themeColors.primary}50`,
                      borderRadius: '6px', color: themeColors.primary,
                      '&:hover': { bgcolor: themeColors.primaryLight + '15', borderColor: themeColors.primary }
                    }}>
                    <AddIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {lead.tags.map(tag => (
                <Chip key={tag.id} label={tag.name} size="small"
                  disabled={removeTagMut.isPending || addTagMut.isPending}
                  onDelete={canModify && !isConverted && lead.status !== 'JUNK' ? () => removeTagMut.mutate(tag.id) : undefined}
                  sx={{
                    height: 22, fontSize: '11px', fontWeight: 600,
                    bgcolor: tag.color + '20', color: tag.color, border: `1px solid ${tag.color}40`,
                    '& .MuiChip-deleteIcon': { fontSize: '13px', color: tag.color + '80', '&:hover': { color: tag.color } }
                  }} />
              ))}
              {lead.tags.length === 0 && !addTagMut.isPending && (
                <Typography fontSize="12px" color="text.secondary" fontStyle="italic" sx={{ py: 0.25 }}>No tags yet</Typography>
              )}
            </Box>
            {canModify && !isConverted && lead.status !== 'JUNK' && allTags.length === 0 && canCreateTag && (
              <Box sx={{ mt: 1.5, p: 1.25, bgcolor: '#eff6ff', borderRadius: '8px', border: '1px solid #dbeafe' }}>
                <Typography fontSize="11.5px" color="#1d4ed8">
                  No tags yet.{' '}
                  <Box component="span" onClick={() => window.location.assign('/settings/tag')}
                    sx={{ fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
                    Create tags
                  </Box>
                </Typography>
              </Box>
            )}
          </SideCard>

        </Box>

        {/* ════ Right Panel ════ */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <EntityDetailTabs
            tabs={leadTabs}
            activeTab={activeTab}
            onChange={setActiveTab}
            padding={2.5}
            tabHeight={44}
            tabFontSize="13px"
            tabFontWeight={600}
            tabPaddingX={2}
            borderRadius="12px"
          />
        </Box>

      </Box>

      {/* ── Dialogs ── */}
      <EditLeadDialog open={editOpen} lead={lead} onClose={() => setEditOpen(false)}
        onSave={async p => { await updateMut.mutateAsync(p) }} isSaving={updateMut.isPending} />

      <AssignDialog open={assignOpen} lead={lead} onClose={() => setAssignOpen(false)}
        onSave={async i => { await assignMut.mutateAsync(i) }} isSaving={assignMut.isPending} />

      <ConvertLeadFlow
        open={convertOpen}
        lead={lead}
        onClose={() => setConvertOpen(false)}
        onSuccess={invalidate}
      />

      <AddActivityDialog open={activityOpen} defaultType={activityType} onClose={() => setActivityOpen(false)}
        onSave={async p => { await activityMut.mutateAsync(p) }} isSaving={activityMut.isPending} performedById={userId} />

      <AddTagModal
        open={tagModalOpen}
        onClose={() => setTagModalOpen(false)}
        allTags={allTags}
        attachedTagIds={attachedTagIds}
        onAdd={(tagId) => addTagMut.mutate(tagId)}
        isAdding={addTagMut.isPending}
      />

      {/* Delete confirm */}
      <DeleteDialog
        open={deleteOpen}
        name={lead.name}
        title="Delete Lead"
        description={`This will permanently delete ${lead.name} and all activity history. This cannot be undone.`}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => deleteMut.mutate()}
        isDeleting={deleteMut.isPending}
      />

      <ConfirmDialog
        open={isJunkConfirmOpen}
        title="Mark Lead as Junk"
        description={`Are you sure you want to mark ${lead.name} as Junk? This will update the lead's status to JUNK.`}
        confirmLabel="Mark as Junk"
        confirmColor="error"
        isConfirming={statusMut.isPending}
        onClose={() => setIsJunkConfirmOpen(false)}
        onConfirm={() => {
          statusMut.mutate('JUNK', {
            onSuccess: () => {
              setIsJunkConfirmOpen(false)
            }
          })
        }}
      />

      {/* Call flow overlays removed (now global) */}

      <SendEmailDrawer
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        defaultTo={lead.email ? [lead.email] : []}
        onSend={(data) => sendEmailMutation.mutate(data)}
        isSending={sendEmailMutation.isPending}
        orgId={lead.orgId}
      />

    </Box>
  )
})

export default LeadDetailPage
